import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { AtomicLogo } from "../../assets/AtomicLogo";
import { useCustomer } from "../../contexts/app/provider";
import { ChatContext } from "../../contexts/chat/context";
import { LayoutContext } from "../../contexts/layout/context";
import { CHAT_FONT_STACK } from "../../contexts/material_ui/theme";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import type { Run } from "../../services/appsync/types";
import { ChatStatus } from "./chat_status";
import { InputContainer } from "./input_container";
import { MessageContainer } from "./message_container";
import { PlatformConversationPicker } from "./platform_conversation_picker";

const routeTitles: Record<string, string> = {
  "/run-history": "run history",
  "/demand": "demand",
  "/demand_preview": "demand",
  "/forecast-tracking": "forecast tracking",
  "/demand-walk-single": "demand walk",
  "/sku-cutovers": "sku cutovers",
  "/supply": "supply",
  "/supply-walk": "supply walk",
  "/supply-walk-single": "supply walk",
  "/supply-walk-single-for-hubs": "supply walk",
  "/inventory-health": "inventory health",
  "/inventory-health-with-transfers": "inventory health",
  "/inventory-health-mrp": "inventory health",
  "/raw-inventory-health": "inventory health",
  "/inventory": "inventory",
  "/transfer-planning": "transfer planning",
  "/transfer-recommendations": "transfer planning",
  "/po-export": "purchase orders",
  "/purchase-order-export-peach-and-lily": "purchase orders",
  "/po-inbox": "PO inbox",
  "/purchasing-calendar": "purchasing calendar",
  "/avanti-card-runs": "avanti card runs",
};

const toTitleCase = (value: string) =>
  value
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getViewLabel = (pathname: string) => {
  const normalized = pathname.replace(/_preview$/, "");
  if (routeTitles[normalized]) {
    return toTitleCase(routeTitles[normalized]);
  }

  const segment = normalized.split("/").filter(Boolean)[0];
  if (!segment) return "Current View";

  return toTitleCase(segment.replace(/-/g, " "));
};

const getRunLabel = (value: unknown) => {
  if (!value || typeof value !== "object") {
    return "No run selected";
  }

  const run = value as {
    name?: string | null;
    displayId?: string | number | null;
  };

  if (run.name) return run.name;
  if (run.displayId) return `Run ${run.displayId}`;

  return "Selected planning run";
};

const getContextLabel = (
  value: unknown,
  selectedCustomerId: string | null,
  currentViewLabel: string,
) => {
  const prefix = [selectedCustomerId, currentViewLabel].filter(Boolean);
  if (!value || typeof value !== "object") {
    return [...prefix, "No run selected"].join(" · ");
  }

  const run = value as {
    name?: string | null;
    displayId?: string | number | null;
  };
  const displayId =
    run.displayId === null || run.displayId === undefined
      ? ""
      : String(run.displayId).trim();
  const runName = typeof run.name === "string" ? run.name.trim() : "";
  const runIdentity = displayId ? `Run ${displayId}` : "Selected planning run";

  return [
    ...prefix,
    runIdentity,
    ...(runName ? [`Name: ${runName}`] : []),
  ].join(" · ");
};

const getConnectionKey = (
  run: Run,
  pathname: string,
  selectedCustomerId: string,
) => {
  const settings = run.runSettings;
  return JSON.stringify([
    selectedCustomerId,
    run.id,
    settings?.organizationId,
    settings?.inputId,
    settings?.workflowId,
    settings?.settingsNameAndVersion,
    settings?.runAttemptId,
    pathname,
  ]);
};

const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  p: 0,
  m: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

type ConnectionAttempt = {
  key: string;
  status: "pending" | "settled";
};

export const Chat = () => {
  const {
    isConnected,
    isConnecting,
    connectChat,
    disconnectChat,
    startNewConversation,
    messages,
    isAwaitingResponse,
    agentState,
    chatContext,
    transportCapabilities,
    transportKind,
  } = useContext(ChatContext);
  const {
    setIsSidebarOpen,
    isNucleusExpanded = false,
    setIsNucleusExpanded,
  } = useContext(LayoutContext);
  const {
    loading: isRunLoading,
    mainRun,
    lastSuccessfulRun,
  } = useContext(VersionedDataContext);
  const { selectedCustomerId } = useCustomer();
  const location = useLocation();
  const [connectionAttempt, setConnectionAttempt] =
    useState<ConnectionAttempt | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);

  const currentRun = mainRun || lastSuccessfulRun;
  const currentRunMatchesSelectedCustomer = Boolean(
    currentRun &&
      selectedCustomerId &&
      currentRun.runSettings?.organizationId === selectedCustomerId,
  );
  const connectionKey = useMemo(
    () =>
      currentRun && selectedCustomerId && currentRunMatchesSelectedCustomer
        ? getConnectionKey(currentRun, location.pathname, selectedCustomerId)
        : null,
    [
      currentRun,
      currentRunMatchesSelectedCustomer,
      location.pathname,
      selectedCustomerId,
    ],
  );
  const currentViewLabel = useMemo(
    () => getViewLabel(location.pathname),
    [location.pathname],
  );
  const currentRunLabel = useMemo(() => getRunLabel(currentRun), [currentRun]);
  const displayedRunLabel =
    isRunLoading && !currentRun ? "Loading planning run..." : currentRunLabel;
  const currentContextLabel = useMemo(
    () => getContextLabel(currentRun, selectedCustomerId, currentViewLabel),
    [currentRun, currentViewLabel, selectedCustomerId],
  );
  const displayedContextLabel =
    isRunLoading && !currentRun
      ? [selectedCustomerId, currentViewLabel, "Loading planning run..."]
          .filter(Boolean)
          .join(" · ")
      : currentContextLabel;
  const shouldAutoConnect =
    transportKind === "platform" &&
    Boolean(currentRun) &&
    !isConnected &&
    !isConnecting &&
    connectionAttempt?.key !== connectionKey;
  const isPreparingConnection =
    shouldAutoConnect ||
    (transportKind === "platform" &&
      !isConnected &&
      !isConnecting &&
      connectionAttempt?.key === connectionKey &&
      connectionAttempt.status === "pending");

  useEffect(() => {
    if (!currentRun || !connectionKey) {
      if (connectionAttempt) setConnectionAttempt(null);
      return;
    }
    if (!shouldAutoConnect) return;

    setConnectionAttempt({ key: connectionKey, status: "pending" });
    void Promise.resolve()
      .then(async () => {
        await connectChat(currentRun, location.pathname);
      })
      .catch(() => undefined)
      .finally(() => {
        setConnectionAttempt((attempt) =>
          attempt?.key === connectionKey
            ? { key: connectionKey, status: "settled" }
            : attempt,
        );
      });
  }, [
    connectChat,
    connectionAttempt,
    connectionKey,
    currentRun,
    location.pathname,
    shouldAutoConnect,
  ]);

  const handleClose = () => {
    setIsNucleusExpanded?.(false);
    setIsSidebarOpen(false);
    if (transportKind !== "platform") disconnectChat();
  };

  const handleExpand = () => {
    setIsNucleusExpanded?.(!isNucleusExpanded);
  };

  const handleRetry = () => {
    if (!currentRun || isConnecting) return;
    void Promise.resolve(connectChat(currentRun, location.pathname));
  };

  const isActuallyConnected = isConnected && Boolean(chatContext);
  const showStatusRow =
    transportKind !== "platform" ||
    !isActuallyConnected ||
    isPreparingConnection;
  const connectionLabel = isPreparingConnection
    ? "Checking availability"
    : isActuallyConnected
      ? "Connected"
      : "Unavailable";
  const showExpandedWelcome =
    isNucleusExpanded &&
    messages.length === 0 &&
    !isAwaitingResponse &&
    agentState.status !== "streaming";

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minWidth: 0,
        bgcolor: "#fff",
        color: "#1F2733",
        fontFamily: CHAT_FONT_STACK,
      }}
    >
      <Box
        component="header"
        sx={{
          minHeight: 54,
          px: { xs: 1.25, sm: 1.75 },
          py: 0.8,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(102, 116, 138, 0.16)",
          bgcolor: "rgba(255, 255, 255, 0.98)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.8,
            flexShrink: 0,
          }}
        >
          <AtomicLogo width={22} height={22} />
          <Typography
            sx={{
              fontFamily: CHAT_FONT_STACK,
              fontSize: "0.93rem",
              lineHeight: 1.2,
              fontWeight: 700,
            }}
          >
            Nucleus
          </Typography>
          {isNucleusExpanded && (
            <Chip
              size="small"
              label="Beta"
              sx={{
                height: 20,
                bgcolor: "rgba(56, 119, 247, 0.1)",
                color: "#2C56B0",
                fontSize: "0.65rem",
                fontWeight: 700,
                "& .MuiChip-label": { px: 0.8 },
              }}
            />
          )}
        </Box>

        <Chip
          role="group"
          aria-label="Nucleus context"
          title={displayedContextLabel}
          size="small"
          label={displayedContextLabel}
          sx={{
            minWidth: 0,
            maxWidth: isNucleusExpanded ? 520 : 190,
            flex: isNucleusExpanded ? "0 1 520px" : "1 1 100px",
            mx: isNucleusExpanded ? "auto" : 0,
            height: 28,
            bgcolor: "#F3F6FA",
            color: "#56657A",
            fontFamily: CHAT_FONT_STACK,
            fontSize: "0.72rem",
            "& .MuiChip-label": {
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        />

        <Typography sx={visuallyHidden}>
          Ask about your supply chain, the selected run, or customer
          documentation. Nucleus will tell you when live data inspection is
          unavailable.
        </Typography>

        <Box
          sx={{
            ml: isNucleusExpanded ? 0 : "auto",
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            flexShrink: 0,
          }}
        >
          {currentRun && (
            <Box
              role="status"
              aria-label={`Nucleus connection: ${connectionLabel}`}
              sx={{
                mr: isNucleusExpanded ? 0.5 : 0.25,
                display: "flex",
                alignItems: "center",
                gap: 0.55,
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: isActuallyConnected
                    ? "#5DBA8A"
                    : isPreparingConnection
                      ? "#D7A93B"
                      : "#9AA5B5",
                }}
              />
              {isNucleusExpanded && (
                <Typography
                  sx={{
                    fontFamily: CHAT_FONT_STACK,
                    fontSize: "0.72rem",
                    color: "#66748A",
                  }}
                >
                  {connectionLabel}
                </Typography>
              )}
            </Box>
          )}

          {transportKind === "platform" &&
            currentRun &&
            transportCapabilities.history &&
            transportCapabilities.resume && (
              <PlatformConversationPicker
                disabled={!isActuallyConnected || isConnecting}
                runLabel={displayedRunLabel}
                scopeKey={connectionKey ?? "no-run"}
              />
            )}

          {transportKind === "platform" && currentRun && (
            <Tooltip title="New chat">
              <span>
                <IconButton
                  aria-label="New chat"
                  size="small"
                  disabled={!isActuallyConnected || isConnecting}
                  onClick={() => void startNewConversation()}
                >
                  <AddCommentOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          <Tooltip
            title={
              isNucleusExpanded
                ? "Return Nucleus to the side panel"
                : "Open Nucleus workspace"
            }
          >
            <IconButton
              aria-label={
                isNucleusExpanded
                  ? "Return Nucleus to the side panel"
                  : "Open Nucleus workspace"
              }
              size="small"
              onClick={handleExpand}
            >
              {isNucleusExpanded ? (
                <CloseFullscreenOutlinedIcon fontSize="small" />
              ) : (
                <OpenInFullOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Close Nucleus">
            <IconButton
              aria-label="Close Nucleus"
              size="small"
              onClick={handleClose}
            >
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {currentRun ? (
        <>
          {showStatusRow && (
            <Box
              sx={{
                px: { xs: 1.5, sm: 2 },
                py: 0.8,
                borderBottom: "1px solid rgba(102, 116, 138, 0.14)",
              }}
            >
              <ChatStatus
                isConnected={isConnected}
                isPreparing={isPreparingConnection}
                onRetry={
                  transportKind === "platform" && !isConnecting
                    ? handleRetry
                    : undefined
                }
              />
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minHeight: 0,
              bgcolor: "#fff",
            }}
          >
            <Box
              ref={messagesScrollRef}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                display: "flex",
                flexDirection: "column",
                px: isNucleusExpanded ? { xs: 1.5, md: 3 } : 1,
                py: isNucleusExpanded ? 1.5 : 0.75,
                scrollbarWidth: "thin",
              }}
            >
              <MessageContainer
                scrollContainerRef={messagesScrollRef}
                surface={isNucleusExpanded ? "expanded" : "sidebar"}
                runLabel={displayedRunLabel}
                welcomeComposer={
                  showExpandedWelcome ? <InputContainer /> : undefined
                }
              />
            </Box>

            {!showExpandedWelcome && (
              <Box
                data-testid="nucleus-composer-footer"
                sx={{
                  px: isNucleusExpanded ? { xs: 2, md: 3 } : 1.25,
                  pt: 1,
                  pb: isNucleusExpanded ? 1.75 : 1.25,
                  flexShrink: 0,
                  borderTop: "1px solid rgba(102, 116, 138, 0.14)",
                  bgcolor: "rgba(255, 255, 255, 0.98)",
                }}
              >
                <Box sx={{ width: "100%", maxWidth: 760, mx: "auto" }}>
                  <InputContainer />
                </Box>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            px: 4,
            pb: 8,
            textAlign: "center",
            bgcolor: "#F8FAFC",
          }}
        >
          {isRunLoading ? (
            <>
              <CircularProgress size={28} thickness={4} />
              <Typography sx={{ mt: 2, fontSize: "1rem", fontWeight: 700 }}>
                Loading your planning run
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  maxWidth: 300,
                  fontSize: "0.9rem",
                  lineHeight: 1.55,
                  color: "text.secondary",
                }}
              >
                Nucleus will be ready as soon as this view finishes loading.
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: "1rem", fontWeight: 700 }}>
                No planning run selected
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  maxWidth: 300,
                  fontSize: "0.9rem",
                  lineHeight: 1.55,
                  color: "text.secondary",
                }}
              >
                Select a completed run, then reopen Nucleus to ask about this
                view.
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
